import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { Toast } from '@openng/optimus-ui/toast';
import { Subscription } from 'rxjs';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { GeneralGridComponent } from 'src/app/core/components/general-grid.component';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  COMMERCIAL_OPERATIONAL_NOTE_SORT_FIELDS,
  CommercialOperationalNoteSortField,
  CommercialOperationalNoteSortOrQuery,
  CommercialOperationalNoteSearch,
  CommercialOperationalNoteSearchConnection,
} from 'src/app/model/commercial-services/commercial-operational-note';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CommercialOperationalNotesApiService } from 'src/app/services/api/commercial-operational-notes-api.service';
import { CommercialOperationalNoteBuilderService } from 'src/app/services/builder/commercial-operational-note-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { BaseTableService } from 'src/app/services/table/base-table.service';
import { COMMERCIAL_OPERATIONAL_NOTES_SEARCH_TABLE_DEFINITION } from '../../definitions/commercial-operational-notes-search-table-definition';

export interface CommercialOperationalNoteDateRange {
  startDate: Date;
  endDate: Date;
}

function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

export function getDefaultCommercialOperationalNoteDateRange(
  now: Date = new Date()
): CommercialOperationalNoteDateRange {
  return { startDate: startOfDay(now), endDate: endOfDay(now) };
}

@Component({
  selector: 'app-commercial-operational-notes-search-container',
  templateUrl: './commercial-operational-notes-search-container.component.html',
  styleUrls: ['./commercial-operational-notes-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BusinessUnitFilterComponent,
    ButtonDirective,
    DatePicker,
    FormsModule,
    GeneralGridComponent,
    Toast,
    TranslatePipe,
  ],
})
export class CommercialOperationalNotesSearchContainerComponent
  extends SearchContainerComponent<CommercialOperationalNoteSearch>
  implements OnInit, OnDestroy
{
  protected readonly _authApi = inject(AuthApiService);
  protected readonly _logAndMessage = inject(LogAndMessageService);
  protected readonly _commercialOperationalNotesApi = inject(
    CommercialOperationalNotesApiService
  );
  protected readonly _commercialOperationalNoteBuilder = inject(
    CommercialOperationalNoteBuilderService
  );
  protected readonly _tabService = inject(TabService);
  protected readonly _translateService = inject(TranslateService);

  readonly buFilter = viewChild<BusinessUnitFilterComponent>('buFilter');

  startDate: Date | null = null;
  endDate: Date | null = null;

  private activeRequest?: Subscription;
  private lastFilterSignature: string | null = null;

  constructor() {
    super();
    this.setToDefaultDate();
  }

  ngOnInit(): void {
    this.setTableService(
      new BaseTableService<CommercialOperationalNoteSearch>()
    );
    this.screenName = this._translateService.instant(
      'COMMERCIAL_SERVICES.LABEL.COMMERCIAL_OPERATIONAL_NOTES'
    );
    this._tabService.updateActiveTabLabel(this.screenName);
    this.columns = COMMERCIAL_OPERATIONAL_NOTES_SEARCH_TABLE_DEFINITION.map(
      (column) => ({
        ...column,
        value: null,
      })
    );

    this.applyPreviousFilters();
    this.applyPreviousData();
  }

  ngOnDestroy(): void {
    this.activeRequest?.unsubscribe();
  }

  get dateRange(): CommercialOperationalNoteDateRange | null {
    return this.startDate && this.endDate
      ? { startDate: this.startDate, endDate: this.endDate }
      : null;
  }

  setToDefaultDate(): void {
    const range = getDefaultCommercialOperationalNoteDateRange();
    this.startDate = range.startDate;
    this.endDate = range.endDate;
  }

  resetDateRangeToToday(): void {
    this.setToDefaultDate();
    this.refresh(true);
  }

  onDateChange(selectedDate: Date | null, isStartDate: boolean): void {
    const date =
      selectedDate && !Number.isNaN(selectedDate.getTime())
        ? selectedDate
        : null;
    if (isStartDate) {
      this.startDate = date ? startOfDay(date) : null;
    } else {
      this.endDate = date ? endOfDay(date) : null;
    }
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = endOfDay(this.startDate);
    }
    this.refresh(true);
  }

  override handleLazyLoad(
    request: PageRequest,
    event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    const range = this.dateRange;
    if (!range) {
      if (event?.lazy) {
        this.tableService.setLastLazyLoad(event.lazy);
      }
      this.activeRequest?.unsubscribe();
      this.loading = false;
      return;
    }

    const query = this.buildQuery(filters, range);
    const filterSignature = JSON.stringify(query);
    if (
      this.lastFilterSignature !== null &&
      this.lastFilterSignature !== filterSignature
    ) {
      request.page = 0;
      if (event?.lazy) {
        event.lazy.first = 0;
        const table = this.grid()?.table();
        if (table) {
          table.first = 0;
        }
      }
    }
    this.lastFilterSignature = filterSignature;

    if (event?.lazy) {
      this.tableService.setLastLazyLoad(event.lazy);
    }

    const sort = this.buildSort(event);
    this.queryNetwork(request, query, sort);
  }

  refresh(resetPage = false): void {
    const previousLazyLoad = this.tableService.getLastLazyLoad() ?? {};
    const lazy = {
      ...previousLazyLoad,
      first: resetPage ? 0 : (previousLazyLoad.first ?? 0),
      rows: previousLazyLoad.rows ?? this.rows,
    };
    this.lazyLoad({ lazy });
  }

  override clearScreen(): void {
    this.setToDefaultDate();
    const buFilter = this.buFilter();
    if (buFilter) {
      buFilter.selectedBusinessUnits = buFilter.businessUnits.map(
        (option) => option.value
      );
    }
    this.elements = [];
    this.totalRecords = 0;

    const previousLazyLoad = this.tableService.getLastLazyLoad() ?? {};
    const resetLazyLoad = {
      ...previousLazyLoad,
      first: 0,
      rows: previousLazyLoad.rows ?? this.rows,
      sortField: '',
      sortOrder: 0,
      filters: {},
    };
    this.tableService.setLastLazyLoad(resetLazyLoad);
    this.lastFilterSignature = null;

    const previousRequest = this.activeRequest;
    this.grid()?.resetTable();
    if (this.activeRequest === previousRequest) {
      this.refresh(true);
    }
  }

  private buildQuery(
    filters: LazyLoadFilters,
    range: CommercialOperationalNoteDateRange
  ): CommercialOperationalNoteSortOrQuery {
    const businessUnits =
      this.buFilter()?.selectedBusinessUnits ??
      this._authApi.getUserBusinessUnits().map((unit) => unit.id);
    return this._commercialOperationalNoteBuilder.buildCommercialOperationalNoteQuery(
      filters,
      range.startDate,
      range.endDate,
      businessUnits
    );
  }

  private buildSort(
    event: GridLazyLoadEvent
  ): CommercialOperationalNoteSortOrQuery {
    const requestedField = event?.lazy?.sortField;
    const isAllowedField = COMMERCIAL_OPERATIONAL_NOTE_SORT_FIELDS.includes(
      requestedField as CommercialOperationalNoteSortField
    );
    const field: CommercialOperationalNoteSortField = isAllowedField
      ? (requestedField as CommercialOperationalNoteSortField)
      : 'updatedAt';
    const direction =
      isAllowedField && event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';

    this.tableService.setLastSortField(field);
    this.tableService.setLastSortDirection(direction === 'ASC' ? 1 : -1);
    return { [field]: direction };
  }

  private queryNetwork(
    request: PageRequest,
    query: CommercialOperationalNoteSortOrQuery,
    sort: CommercialOperationalNoteSortOrQuery
  ): void {
    this.activeRequest?.unsubscribe();
    this.loading = true;

    this.activeRequest = this._commercialOperationalNotesApi
      .queryForCommercialOperationalNotes(
        request.page + 1,
        request.pageSize,
        query,
        sort
      )
      .subscribe({
        next: (response: CommercialOperationalNoteSearchConnection) => {
          this.elements = response.items;
          this.finalizeQuery(response.totalRecords);
        },
        error: (error: unknown) => {
          this.elements = [];
          this.totalRecords = 0;
          this.loading = false;
          this._logAndMessage.errorLogOnly(String(error));
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.QUERY_COMMERCIAL_OPERATIONAL_NOTES',
          });
        },
      });
  }
}
